import type { AqItem, AqItemType } from '@elva/ui';
import { ApprovalQueueView, GrowthDashboardTopBarActions } from '@elva/ui';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { deepCamel, str } from '@/data/adapt';
import {
  articleIdFromReviewPath,
  landingPageIdFromReviewPath,
  resolveReviewPath,
} from '@/features/shared/resolveReviewPath';
import { useApprovalMutations, useApprovals } from '../hooks/useApprovals';

function landingPageIdFromApprove(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const row = deepCamel<Record<string, unknown>>(data as Record<string, unknown>);
  return str(row.landingPageId ?? row.landing_page_id ?? row.entityId, '').trim();
}

export function ApprovalQueuePage() {
  const navigate = useNavigate();

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const [activeTab, setActiveTab] = useState<AqItemType | 'all'>('all');
  const { data, isPending, isError, error, refetch, isRefetching, isFetching } =
    useApprovals(activeTab);
  const { approve, reject } = useApprovalMutations();

  const handleReview = useCallback(
    (item: AqItem) => {
      if (item.type === 'article') {
        const articleId = articleIdFromReviewPath(item.reviewTo);
        if (articleId) {
          navigate(`/articles/${articleId}`);
          return;
        }
      }
      if (item.type === 'landing-page') {
        const landingPageId = landingPageIdFromReviewPath(item.reviewTo);
        if (landingPageId) {
          navigate(`/landing-pages/${landingPageId}`);
          return;
        }
        navigate('/landing-pages');
        return;
      }
      if (item.type === 'gbp-post' || item.type === 'review-reply') {
        navigate(resolveReviewPath(item.reviewTo));
        return;
      }
      navigate(resolveReviewPath(item.reviewTo));
    },
    [navigate],
  );

  const handleApprove = useCallback(
    (id: string) => {
      approve.mutate(id, {
        onSuccess: (result) => {
          const landingPageId = landingPageIdFromApprove(result);
          if (landingPageId) {
            navigate(`/landing-pages/${landingPageId}`);
          }
        },
      });
    },
    [approve, navigate],
  );

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
      isEmpty={data !== undefined && data.items.length === 0}
    >
      <ApprovalQueueView
        {...viewProps({
          items: data?.items,
          tabCounts: data?.tabCounts,
          activeTab,
          onTabChange: setActiveTab,
          loading: isFetching || approve.isPending || reject.isPending,
          onApprove: handleApprove,
          onReject: (id: string) => reject.mutate({ id }),
          onReview: handleReview,
        })}
      />
    </AsyncBoundary>
  );
}
